'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  User,
  Building2,
  CheckCircle2,
  TrendingUp,
  ShieldCheck,
  Zap,
  ArrowRight,
  Copy,
  Check,
  AlertCircle,
  RefreshCw,
  WifiOff,
} from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';


type LoginFormData = {
  email: string;
  password: string;
  remember: boolean;
};

type SignupFormData = {
  name: string;
  company: string;
  email: string;
  password: string;
  confirmPassword: string;
  terms: boolean;
};

interface DemoCredential {
  role: string;
  email: string;
  password: string;
  description: string;
}

const DEMO_CREDENTIALS: DemoCredential[] = [
  {
    role: 'Admin',
    email: 'jordan.rivera@acmecorp.io',
    password: 'BudgetIQ@Admin2026',
    description: 'Full access — upload statements, manage categories, invite team',
  },
  {
    role: 'Viewer',
    email: 'maya.chen@acmecorp.io',
    password: 'BudgetIQ@View2026',
    description: 'Read-only — view transactions, reports, and budget summaries',
  },
];

const FEATURES = [
  { icon: Zap, text: 'AI classifies transactions in seconds' },
  { icon: TrendingUp, text: 'Track spend vs budget across all accounts' },
  { icon: ShieldCheck, text: 'Role-based access for your accountant' },
];

interface CopiedState {
  [key: string]: boolean;
}

// ─── Error helpers ────────────────────────────────────────────────────────────

type ErrorKind = 'network' | 'validation' | 'unknown';

interface ParsedError {
  kind: ErrorKind;
  message: string;
  retryable: boolean;
}

function parseSupabaseError(err: unknown): ParsedError {
  const msg = err instanceof Error ? err.message : String(err);
  const lower = msg.toLowerCase();

  // Network / connectivity
  if (
    lower.includes('fetch') ||
    lower.includes('network') ||
    lower.includes('failed to fetch') ||
    lower.includes('networkerror') ||
    lower.includes('timeout') ||
    lower.includes('econnrefused') ||
    lower.includes('load failed')
  ) {
    return {
      kind: 'network',
      message: 'Unable to reach the server. Check your internet connection and try again.',
      retryable: true,
    };
  }

  // Validation / auth errors
  if (lower.includes('invalid login credentials') || lower.includes('invalid credentials')) {
    return { kind: 'validation', message: 'Incorrect email or password. Please try again.', retryable: false };
  }
  if (lower.includes('email not confirmed')) {
    return { kind: 'validation', message: 'Please verify your email address before signing in.', retryable: false };
  }
  if (lower.includes('user already registered') || lower.includes('already been registered')) {
    return { kind: 'validation', message: 'An account with this email already exists. Try signing in instead.', retryable: false };
  }
  if (lower.includes('password should be at least') || lower.includes('weak password')) {
    return { kind: 'validation', message: 'Password is too weak. Use at least 8 characters with a number and symbol.', retryable: false };
  }
  if (lower.includes('rate limit') || lower.includes('too many requests')) {
    return { kind: 'validation', message: 'Too many attempts. Please wait a moment before trying again.', retryable: true };
  }
  if (lower.includes('signup is disabled') || lower.includes('signups not allowed')) {
    return { kind: 'validation', message: 'New sign-ups are currently disabled. Contact your administrator.', retryable: false };
  }

  return { kind: 'unknown', message: msg || 'Something went wrong. Please try again.', retryable: true };
}

// ─── Inline error banner ──────────────────────────────────────────────────────

interface ErrorBannerProps {
  error: ParsedError;
  onRetry?: () => void;
  onDismiss: () => void;
}

function ErrorBanner({ error, onRetry, onDismiss }: ErrorBannerProps) {
  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-800/40 dark:bg-red-950/30 dark:text-red-300"
    >
      {error.kind === 'network' ? (
        <WifiOff size={16} className="mt-0.5 shrink-0 text-red-500" />
      ) : (
        <AlertCircle size={16} className="mt-0.5 shrink-0 text-red-500" />
      )}
      <span className="flex-1 leading-relaxed">{error.message}</span>
      <div className="flex items-center gap-1 shrink-0">
        {error.retryable && onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="flex items-center gap-1 rounded px-2 py-1 text-xs font-semibold text-red-700 hover:bg-red-100 transition-colors dark:text-red-300 dark:hover:bg-red-900/40"
          >
            <RefreshCw size={11} />
            Retry
          </button>
        )}
        <button
          type="button"
          onClick={onDismiss}
          className="rounded px-2 py-1 text-xs font-semibold text-red-600 hover:bg-red-100 transition-colors dark:text-red-400 dark:hover:bg-red-900/40"
          aria-label="Dismiss error"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

export default function AuthForm() {
  const [tab, setTab] = useState<'login' | 'signup' | 'forgot'>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState<CopiedState>({});
  const [loginError, setLoginError] = useState<ParsedError | null>(null);
  const [signupError, setSignupError] = useState<ParsedError | null>(null);
  const [forgotError, setForgotError] = useState<ParsedError | null>(null);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const router = useRouter();
  const { signIn, signUp, resetPassword } = useAuth();

  const loginForm = useForm<LoginFormData>({
    defaultValues: { email: '', password: '', remember: false },
  });

  const signupForm = useForm<SignupFormData>({
    defaultValues: { name: '', company: '', email: '', password: '', confirmPassword: '', terms: false },
  });

  const handleCopy = (text: string, key: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied((prev) => ({ ...prev, [key]: true }));
      setTimeout(() => setCopied((prev) => ({ ...prev, [key]: false })), 2000);
    });
  };

  const handleUseCredential = (cred: DemoCredential) => {
    loginForm.setValue('email', cred.email);
    loginForm.setValue('password', cred.password);
    if (tab !== 'login') setTab('login');
  };

  // ── Login with Supabase + retry ──────────────────────────────────────────
  const performLogin = async (data: LoginFormData) => {
    setIsLoading(true);
    setLoginError(null);
    try {
      await signIn(data.email, data.password);
      router.push('/budget-dashboard');
    } catch (err) {
      const parsed = parseSupabaseError(err);
      setLoginError(parsed);
    } finally {
      setIsLoading(false);
    }
  };

  const onLoginSubmit = (data: LoginFormData) => performLogin(data);

  const retryLogin = () => {
    const values = loginForm.getValues();
    performLogin(values);
  };

  // ── Signup with Supabase + retry ─────────────────────────────────────────
  const performSignup = async (data: SignupFormData) => {
    if (data.password !== data.confirmPassword) {
      signupForm.setError('confirmPassword', { message: 'Passwords do not match' });
      return;
    }
    setIsLoading(true);
    setSignupError(null);
    try {
      await signUp(data.email, data.password, { fullName: data.name });
      setSignupSuccess(true);
    } catch (err) {
      const parsed = parseSupabaseError(err);
      setSignupError(parsed);
    } finally {
      setIsLoading(false);
    }
  };

  const onSignupSubmit = (data: SignupFormData) => performSignup(data);

  const retrySignup = () => {
    const values = signupForm.getValues();
    performSignup(values);
  };

  // ── Forgot password ──────────────────────────────────────────────────────
  const [forgotEmail, setForgotEmail] = useState('');

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setIsLoading(true);
    setForgotError(null);
    try {
      await resetPassword(forgotEmail);
      setForgotSuccess(true);
    } catch (err) {
      const parsed = parseSupabaseError(err);
      setForgotError(parsed);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-[45%] xl:w-[40%] gradient-brand flex-col justify-between p-10 xl:p-14 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-[-80px] right-[-80px] w-[300px] h-[300px] rounded-full bg-white/5" />
          <div className="absolute bottom-[-60px] left-[-60px] w-[240px] h-[240px] rounded-full bg-white/5" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-white/3" />
        </div>

        {/* Logo */}
        <div className="flex items-center gap-3 relative z-10">
          <AppLogo size={40} />
          <span className="text-xl font-bold text-white">BudgetIQ</span>
        </div>

        {/* Main copy */}
        <div className="relative z-10 space-y-8">
          <div>
            <h1 className="text-3xl xl:text-4xl font-bold text-white leading-tight text-balance">
              Business budgeting that actually makes sense
            </h1>
            <p className="mt-4 text-base text-white/70 leading-relaxed">
              Upload your bank statements, let AI handle the classification, and see exactly where your money goes — every month, across every account.
            </p>
          </div>

          <ul className="space-y-4">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <li key={`feature-${f.text.slice(0, 20)}`} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
                    <Icon size={16} className="text-white" />
                  </div>
                  <span className="text-sm text-white/85 font-medium">{f.text}</span>
                </li>
              );
            })}
          </ul>

          {/* Social proof */}
          <div className="flex items-center gap-3 pt-2">
            <div className="flex -space-x-2">
              {['#3B82F6', '#8B5CF6', '#10B981', '#F59E0B'].map((color, i) => (
                <div
                  key={`avatar-${i + 1}`}
                  className="w-8 h-8 rounded-full border-2 border-white/30 flex items-center justify-center text-xs font-bold text-white"
                  style={{ backgroundColor: color }}
                >
                  {['JR', 'MC', 'AS', 'PL'][i]}
                </div>
              ))}
            </div>
            <p className="text-xs text-white/70">
              <span className="font-semibold text-white">1,200+ businesses</span> track their budget with BudgetIQ
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="relative z-10">
          <p className="text-xs text-white/40">© 2026 BudgetIQ Inc. · Privacy · Terms</p>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex flex-col justify-center items-center px-6 py-10 bg-background overflow-y-auto">
        <div className="w-full max-w-[440px] space-y-6 fade-in">
          {/* Mobile logo */}
          <div className="flex lg:hidden items-center justify-center gap-2 mb-2">
            <AppLogo size={32} />
            <span className="text-lg font-bold text-foreground">BudgetIQ</span>
          </div>

          {/* Tabs */}
          <div className="flex bg-muted p-1 rounded-xl">
            {(['login', 'signup'] as const).map((t) => (
              <button
                key={`tab-${t}`}
                onClick={() => { setTab(t); setLoginError(null); setSignupError(null); setSignupSuccess(false); setForgotSuccess(false); setForgotError(null); }}
                className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all duration-200 ${
                  tab === t
                    ? 'bg-card text-foreground shadow-card'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {t === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            ))}
          </div>

          {/* Login form */}
          {tab === 'login' && (
            <form onSubmit={loginForm.handleSubmit(onLoginSubmit)} className="space-y-4">
              <div>
                <h2 className="text-2xl font-bold text-foreground">Welcome back</h2>
                <p className="text-sm text-muted-foreground mt-1">Sign in to your BudgetIQ workspace</p>
              </div>

              {/* Inline error banner */}
              {loginError && (
                <ErrorBanner
                  error={loginError}
                  onRetry={loginError.retryable ? retryLogin : undefined}
                  onDismiss={() => setLoginError(null)}
                />
              )}

              {/* Email */}
              <div>
                <label htmlFor="login-email" className="label-text">Work email</label>
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                  <input
                    id="login-email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@company.com"
                    className="input-field pl-9"
                    {...loginForm.register('email', {
                      required: 'Email is required',
                      pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
                    })}
                  />
                </div>
                {loginForm.formState.errors.email && (
                  <p className="error-text">
                    <CheckCircle2 size={12} />
                    {loginForm.formState.errors.email.message}
                  </p>
                )}
              </div>

              {/* Password */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label htmlFor="login-password" className="label-text mb-0">Password</label>
                  <button
                    type="button"
                    onClick={() => { setTab('forgot'); setLoginError(null); setForgotEmail(loginForm.getValues('email') || ''); }}
                    className="text-xs text-primary font-medium hover:underline"
                  >
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="••••••••••••"
                    className="input-field pl-9 pr-10"
                    {...loginForm.register('password', {
                      required: 'Password is required',
                      minLength: { value: 8, message: 'Password must be at least 8 characters' },
                    })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {loginForm.formState.errors.password && (
                  <p className="error-text">
                    <CheckCircle2 size={12} />
                    {loginForm.formState.errors.password.message}
                  </p>
                )}
              </div>

              {/* Remember me */}
              <div className="flex items-center gap-2">
                <input
                  id="login-remember"
                  type="checkbox"
                  className="w-4 h-4 rounded border-input accent-primary cursor-pointer"
                  {...loginForm.register('remember')}
                />
                <label htmlFor="login-remember" className="text-sm text-muted-foreground cursor-pointer">
                  Keep me signed in for 30 days
                </label>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary w-full justify-center"
                style={{ minHeight: 42 }}
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Signing in...
                  </>
                ) : (
                  <>
                    Sign In
                    <ArrowRight size={16} />
                  </>
                )}
              </button>
            </form>
          )}

          {/* Forgot password form */}
          {tab === 'forgot' && (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <h2 className="text-2xl font-bold text-foreground">Reset your password</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Enter your work email and we'll send you a reset link.
                </p>
              </div>

              {forgotSuccess ? (
                <div
                  role="status"
                  className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800 dark:border-green-800/40 dark:bg-green-950/30 dark:text-green-300"
                >
                  <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-green-500" />
                  <span className="leading-relaxed">
                    Recovery email sent! Check your inbox and follow the link to reset your password.
                  </span>
                </div>
              ) : (
                <>
                  {forgotError && (
                    <ErrorBanner
                      error={forgotError}
                      onDismiss={() => setForgotError(null)}
                    />
                  )}

                  <div>
                    <label htmlFor="forgot-email" className="label-text">Work email</label>
                    <div className="relative">
                      <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                      <input
                        id="forgot-email"
                        type="email"
                        autoComplete="email"
                        placeholder="you@company.com"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        required
                        className="input-field pl-9"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading || !forgotEmail}
                    className="btn-primary w-full justify-center"
                    style={{ minHeight: 42 }}
                  >
                    {isLoading ? (
                      <>
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Reset Link
                        <ArrowRight size={16} />
                      </>
                    )}
                  </button>
                </>
              )}

              <p className="text-center text-xs text-muted-foreground">
                <button
                  type="button"
                  onClick={() => { setTab('login'); setForgotError(null); setForgotSuccess(false); }}
                  className="text-primary font-semibold hover:underline"
                >
                  ← Back to Sign In
                </button>
              </p>
            </form>
          )}

          {/* Signup form */}
          {tab === 'signup' && (
            <form onSubmit={signupForm.handleSubmit(onSignupSubmit)} className="space-y-4">
              <div>
                <h2 className="text-2xl font-bold text-foreground">Create your workspace</h2>
                <p className="text-sm text-muted-foreground mt-1">Get your business budgeting in order today</p>
              </div>

              {/* Signup success state */}
              {signupSuccess && (
                <div
                  role="status"
                  className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800 dark:border-green-800/40 dark:bg-green-950/30 dark:text-green-300"
                >
                  <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-green-500" />
                  <span className="leading-relaxed">
                    Account created! Check your email to verify your address, then sign in.
                  </span>
                </div>
              )}

              {/* Inline error banner */}
              {signupError && (
                <ErrorBanner
                  error={signupError}
                  onRetry={signupError.retryable ? retrySignup : undefined}
                  onDismiss={() => setSignupError(null)}
                />
              )}

              {/* Name */}
              <div>
                <label htmlFor="signup-name" className="label-text">Full name</label>
                <div className="relative">
                  <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                  <input
                    id="signup-name"
                    type="text"
                    placeholder="Jordan Rivera"
                    className="input-field pl-9"
                    {...signupForm.register('name', { required: 'Full name is required' })}
                  />
                </div>
                {signupForm.formState.errors.name && (
                  <p className="error-text"><CheckCircle2 size={12} />{signupForm.formState.errors.name.message}</p>
                )}
              </div>

              {/* Company */}
              <div>
                <label htmlFor="signup-company" className="label-text">Company name</label>
                <div className="relative">
                  <Building2 size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                  <input
                    id="signup-company"
                    type="text"
                    placeholder="Acme Corp"
                    className="input-field pl-9"
                    {...signupForm.register('company', { required: 'Company name is required' })}
                  />
                </div>
                {signupForm.formState.errors.company && (
                  <p className="error-text"><CheckCircle2 size={12} />{signupForm.formState.errors.company.message}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label htmlFor="signup-email" className="label-text">Work email</label>
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                  <input
                    id="signup-email"
                    type="email"
                    placeholder="you@company.com"
                    className="input-field pl-9"
                    {...signupForm.register('email', {
                      required: 'Email is required',
                      pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
                    })}
                  />
                </div>
                {signupForm.formState.errors.email && (
                  <p className="error-text"><CheckCircle2 size={12} />{signupForm.formState.errors.email.message}</p>
                )}
              </div>

              {/* Password */}
              <div>
                <label htmlFor="signup-password" className="label-text">Password</label>
                <p className="helper-text mb-1.5">At least 8 characters, including a number and symbol</p>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                  <input
                    id="signup-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••••••"
                    className="input-field pl-9 pr-10"
                    {...signupForm.register('password', {
                      required: 'Password is required',
                      minLength: { value: 8, message: 'At least 8 characters required' },
                    })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {signupForm.formState.errors.password && (
                  <p className="error-text"><CheckCircle2 size={12} />{signupForm.formState.errors.password.message}</p>
                )}
              </div>

              {/* Confirm password */}
              <div>
                <label htmlFor="signup-confirm" className="label-text">Confirm password</label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                  <input
                    id="signup-confirm"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="••••••••••••"
                    className="input-field pl-9 pr-10"
                    {...signupForm.register('confirmPassword', { required: 'Please confirm your password' })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {signupForm.formState.errors.confirmPassword && (
                  <p className="error-text"><CheckCircle2 size={12} />{signupForm.formState.errors.confirmPassword.message}</p>
                )}
              </div>

              {/* Terms */}
              <div>
                <div className="flex items-start gap-2">
                  <input
                    id="signup-terms"
                    type="checkbox"
                    className="w-4 h-4 mt-0.5 rounded border-input accent-primary cursor-pointer shrink-0"
                    {...signupForm.register('terms', { required: 'You must accept the terms to continue' })}
                  />
                  <label htmlFor="signup-terms" className="text-sm text-muted-foreground cursor-pointer leading-relaxed">
                    I agree to BudgetIQ's{' '}
                    <span className="text-primary font-medium hover:underline cursor-pointer">Terms of Service</span>
                    {' '}and{' '}
                    <span className="text-primary font-medium hover:underline cursor-pointer">Privacy Policy</span>
                  </label>
                </div>
                {signupForm.formState.errors.terms && (
                  <p className="error-text mt-1"><CheckCircle2 size={12} />{signupForm.formState.errors.terms.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading || signupSuccess}
                className="btn-primary w-full justify-center"
                style={{ minHeight: 42 }}
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Creating workspace...
                  </>
                ) : (
                  <>
                    Create Workspace
                    <ArrowRight size={16} />
                  </>
                )}
              </button>
            </form>
          )}

          {/* Demo accounts — visible in development only */}
          {process.env.NODE_ENV !== 'production' && (
          <>
          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-background px-3 text-xs text-muted-foreground font-medium">Demo accounts</span>
            </div>
          </div>

          {/* Demo credentials */}
          <div className="card-base overflow-hidden">
            <div className="px-4 py-2.5 bg-muted/60 border-b border-border">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Try BudgetIQ with a demo account
              </p>
            </div>
            <div className="divide-y divide-border">
              {DEMO_CREDENTIALS.map((cred) => (
                <div key={`cred-${cred.role}`} className="p-3 hover:bg-muted/40 transition-colors">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${
                          cred.role === 'Admin' ?'bg-primary/10 text-primary' :'bg-accent/10 text-accent'
                        }`}>
                          {cred.role}
                        </span>
                        <span className="text-xs text-muted-foreground">{cred.description}</span>
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground w-14 shrink-0">Email</span>
                          <code className="text-xs font-mono text-foreground bg-muted px-1.5 py-0.5 rounded truncate flex-1">
                            {cred.email}
                          </code>
                          <button
                            type="button"
                            onClick={() => handleCopy(cred.email, `${cred.role}-email`)}
                            className="shrink-0 p-1 rounded text-muted-foreground hover:text-foreground hover:bg-border transition-colors"
                            aria-label={`Copy ${cred.role} email`}
                          >
                            {copied[`${cred.role}-email`] ? <Check size={12} className="text-positive" /> : <Copy size={12} />}
                          </button>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground w-14 shrink-0">Password</span>
                          <code className="text-xs font-mono text-foreground bg-muted px-1.5 py-0.5 rounded truncate flex-1">
                            {cred.password}
                          </code>
                          <button
                            type="button"
                            onClick={() => handleCopy(cred.password, `${cred.role}-pass`)}
                            className="shrink-0 p-1 rounded text-muted-foreground hover:text-foreground hover:bg-border transition-colors"
                            aria-label={`Copy ${cred.role} password`}
                          >
                            {copied[`${cred.role}-pass`] ? <Check size={12} className="text-positive" /> : <Copy size={12} />}
                          </button>
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleUseCredential(cred)}
                      className="btn-secondary text-xs py-1.5 px-3 shrink-0"
                    >
                      Use
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          </>
          )}

          <p className="text-center text-xs text-muted-foreground">
            {tab === 'login' ? (
              <>
                Don't have an account?{' '}
                <button
                  type="button"
                  onClick={() => setTab('signup')}
                  className="text-primary font-semibold hover:underline"
                >
                  Create one free
                </button>
              </>
            ) : (
              <>
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => setTab('login')}
                  className="text-primary font-semibold hover:underline"
                >
                  Sign in
                </button>
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}